// Discriminated union wrapping useDataModel's {data, loading, error, refetch} shape. Forces error
// handling at the type level: `error`/`refetch` only exist on the 'error' branch, so a consumer
// can't destructure `data` while silently ignoring `error` — the baseline integration this repo
// mirrors fetches order-details errors but never surfaces them; this type makes that gap
// unrepresentable.
export type QueryResult<TData> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: Error; refetch: () => void }
  | { status: 'success'; data: TData; refetch: () => void };

interface UseDataModelResponse<TData> {
  data?: TData;
  loading?: boolean;
  error?: Error;
  refetch?: () => Promise<unknown> | void;
}

export function toQueryResult<TData>(response: UseDataModelResponse<TData>, skipped: boolean): QueryResult<TData> {
  if (skipped) {
    return { status: 'idle' };
  }

  const refetch = () => {
    response.refetch?.();
  };

  // useDataModel's useQuery wrapper (@thd-nucleus/data-sources) deliberately keeps the previous
  // page's `data` populated while `loading` is true on a variables change (e.g. a new
  // pageNumber/pageSize) — so an in-flight request must be checked before `data`, not after, or a
  // page/rows-per-page change would report stale `status: 'success'` instead of `status: 'loading'`,
  // and a spinner gated on `status === 'loading'` would never show during pagination.
  if (response.loading) {
    return { status: 'loading' };
  }

  if (response.error) {
    return { status: 'error', error: response.error, refetch };
  }

  if (response.data) {
    return { status: 'success', data: response.data, refetch };
  }

  return { status: 'loading' };
}
